// @mui
import { useTheme } from "@mui/material/styles";
import {
  Stack,
  AppBar,
  Toolbar,
  IconButton,
  Box,
  Typography,
  Tooltip,
} from "@mui/material";
// utils
import { bgBlur } from "../../../utils/cssStyles";
// hooks
import useOffSetTop from "../../../hooks/useOffSetTop";
import useResponsive from "../../../hooks/useResponsive";
// config
import { HEADER, NAV } from "../../../config";
// components
import Logo from "../../../components/logo";
import Iconify from "../../../components/iconify";
import { useSettingsContext } from "../../../components/settings";
//
import Searchbar from "./Searchbar";
import AccountPopover from "./AccountPopover";
import LanguagePopover from "./LanguagePopover";
import NavHorizontal from "../nav/NavHorizontal";
import { useState } from "react";
import NavVertical from "../nav/NavVertical";
import { NavSectionHorizontal } from "../../../components/nav-section";
import { useAuthContext } from "../../../auth/useAuthContext";
import Notification from "../nav/Notification";
import fetcher from "../../../api/fetcher";
import { END_POINTS } from "../../../api/EndPoints";
import navConfig from "../nav/config";

// ----------------------------------------------------------------------

type Props = {
  onOpenNav?: VoidFunction;
};

type notification = {
  _id: string;
  receiver: string;
  title: string;
  body: string;
  data: {
    invoiceRequestId: string;
    invoiceRequest: any;
    connectionRequestId: string;
  };
  read: boolean;
  type: string;
  status: string;
  createdAt: string;
  updatedAt: string;
  __v: 0;
};

export default function Header({ onOpenNav }: Props) {
  const { user } = useAuthContext();
  const theme = useTheme();
  const [notification, setNotification] = useState<notification[]>([]);
  const [totalPages, setTotalPages] = useState(0);
  const [page, setPage] = useState(1);

  const { themeLayout } = useSettingsContext();
  const isNavHorizontal = themeLayout === "horizontal";
  const isNavMini = themeLayout === "mini";
  const isDesktop = useResponsive("up", "md");
  const isOffset = useOffSetTop(HEADER.H_DASHBOARD_DESKTOP) && !isNavHorizontal;

  const getNotifications = async (page: number) => {
    try {
      const Response = await fetcher.get(
        END_POINTS.DASHBOARD.NOTIFICATION.GET(page, 5),
      );
      setNotification(Response.data.data?.notifications);
      setTotalPages(Response.data.data?.pages);
    } catch (error) {}
  };

  const getMoreNotifications = async (page: number) => {
    try {
      const Response = await fetcher.get(
        END_POINTS.DASHBOARD.NOTIFICATION.GET(page, 5),
      );
      setNotification((prevstate) => [
        ...prevstate,
        ...Response.data.data?.notifications,
      ]);
      setTotalPages(Response.data.data?.pages);
    } catch (error) {}
  };

  const markAsOpen = async (id: string) => {
    try {
      const Response = await fetcher.patch(
        END_POINTS.DASHBOARD.NOTIFICATION.MARK_AS_OPEN(id),
      );
      setNotification(
        notification.map((item) =>
          item._id == id ? { ...item, read: true } : item,
        ),
      );
    } catch (error) {}
  };

  const markAllAsRead = async () => {
    try {
      const Response = await fetcher.get(
        END_POINTS.DASHBOARD.NOTIFICATION.MARK_ALL_AS_READ,
      );
      setNotification(notification.map((item) => ({ ...item, read: true })));
    } catch (error) {}
  };

  const renderContent = (
    <>
      {isDesktop && isNavHorizontal && <Logo sx={{ mr: 18 }} />}

      {!isDesktop && (
        <IconButton onClick={onOpenNav} sx={{ mr: 1, color: "text.primary" }}>
          <Iconify icon="eva:menu-2-fill" />
        </IconButton>
      )}

      <Stack
        flexGrow={1}
        direction="row"
        alignItems="center"
        justifyContent="flex-end"
        spacing={{ xs: 0.5, sm: 1.5 }}
        mt={2}
      >
        <Stack flexGrow={1}>
          {isDesktop && <NavSectionHorizontal data={navConfig} />}
        </Stack>

        <Stack flexDirection={"row"} alignItems={"center"} gap={1}>
          <Box sx={{ width: "100%" }}>
            <Tooltip title={user.username} placement="bottom">
              <Typography
                color="text.secondary"
                variant="body2"
                textAlign={"end"}
                sx={{
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  maxWidth: "250px", // Optional: control width
                }}
              >
                {user.username}
              </Typography>
            </Tooltip>
            <Typography
              color="text.primary"
              variant="body2"
              sx={{ textAlign: "right" }}
            >
              {user?.gstNumber || user?.panNumber}
            </Typography>
          </Box>

          <AccountPopover />
          <span
            onClick={(e) => {
              getNotifications(page);
            }}
          >
            <Notification
              notification={notification}
              markAsOpen={markAsOpen}
              markAllAsRead={markAllAsRead}
              isMorePages={!!(totalPages > page)}
              moreNotification={() => {
                getMoreNotifications(page + 1);
                setPage(page + 1);
              }}
            />
          </span>
        </Stack>
      </Stack>
    </>
  );

  return (
    <AppBar
      sx={{
        boxShadow: "none",
        height: HEADER.H_MOBILE,
        zIndex: theme.zIndex.appBar + 1,
        ...bgBlur({
          color: theme.palette.background.default,
        }),
        transition: theme.transitions.create(["height"], {
          duration: theme.transitions.duration.shorter,
        }),
        ...(isDesktop && {
          width: `calc(100% - ${NAV.W_DASHBOARD + 1}px)`,
          height: HEADER.H_DASHBOARD_DESKTOP,
          ...(isOffset && {
            height: HEADER.H_DASHBOARD_DESKTOP_OFFSET,
          }),
          ...(isNavHorizontal && {
            width: 1,
            bgcolor: "background.default",
            height: HEADER.H_DASHBOARD_DESKTOP_OFFSET,
            borderBottom: (theme) => `dashed 1px ${theme.palette.divider}`,
          }),
          ...(isNavMini && {
            width: `calc(100% - ${NAV.W_DASHBOARD_MINI + 1}px)`,
          }),
        }),
      }}
    >
      <Toolbar
        sx={{
          height: 1,
          px: { lg: 5 },
          backgroundColor: theme.palette.background.neutral,
        }}
      >
        {renderContent}
      </Toolbar>
    </AppBar>
  );
}
